import { getOS, OS } from "common/utils/context";
import * as THREE from "three";
import type { THREEMeshMaterial } from "../../materials/designMaterialParams";
import {
  HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH,
  HIGHARC_ACES_FILMIC_DIVISOR_A,
  HIGHARC_ACES_FILMIC_DIVISOR_B,
  HIGHARC_ACES_FILMIC_DIVISOR_C,
  TONE_MAPPING_ALGORITHM,
} from "../../toneMappingConstants";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import { setToneMappingDefine, type ToneMappingDefines } from "./ToneMappingDefines";

export interface SoftShadowParams {
  useAdvancedPCSS: boolean;
  useSmoothstepShadowComparison: boolean;
  pcssPenumbraAffectedByDistance: boolean;
  pcssNumSamples: number;
}

export const defaultSoftShadowParams: SoftShadowParams = {
  useAdvancedPCSS: true,
  useSmoothstepShadowComparison: true,
  pcssPenumbraAffectedByDistance: true,
  pcssNumSamples: 7,
};

type BooleanDefineValue = 0 | 1;

export interface SharedDefines extends ToneMappingDefines {
  SSFX_MATERIAL_DEV: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention

  PCSS_NUM_SAMPLES: number; // eslint-disable-line @typescript-eslint/naming-convention
  PCSS_NUM_RINGS: number; // eslint-disable-line @typescript-eslint/naming-convention

  ISOLATE_MODE: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention

  PARALLAX_EFFECT_ENABLED: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention

  SSAO: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  SSR: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  SCREEN_SPACE_SHADOWS: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  SMALL_SCALE_AO: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  LIGHT_PROBES_ENABLED: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  USE_ADVANCED_PCSS: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  USE_SMOOTHSTEP_SHADOW_COMPARISON: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention
  PCSS_PENUMBRA_AFFECTED_BY_DISTANCE: BooleanDefineValue; // eslint-disable-line @typescript-eslint/naming-convention

  HIGHARC_ACES_FILMIC_DIVISOR_A: string; // eslint-disable-line @typescript-eslint/naming-convention
  HIGHARC_ACES_FILMIC_DIVISOR_B: string; // eslint-disable-line @typescript-eslint/naming-convention
  HIGHARC_ACES_FILMIC_DIVISOR_C: string; // eslint-disable-line @typescript-eslint/naming-convention
  HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH: string; // eslint-disable-line @typescript-eslint/naming-convention
}

type BooleanSharedDefineKey = keyof Omit<
  SharedDefines,
  | "PCSS_NUM_SAMPLES"
  | "PCSS_NUM_RINGS"
  | "HIGHARC_ACES_FILMIC_DIVISOR_A"
  | "HIGHARC_ACES_FILMIC_DIVISOR_B"
  | "HIGHARC_ACES_FILMIC_DIVISOR_C"
  | "HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH"
  | "DM_LINEAR_TONE_MAPPING"
  | "DM_REINHARD_TONE_MAPPING"
  | "DM_CINEON_TONE_MAPPING"
  | "DM_ACES_FILMIC_TONE_MAPPING"
>;

export function assignMaterialSharedDefines(materialSharedDefines: MaterialSharedDefines, material: THREEMeshMaterial) {
  // This should be used to link the shared defines to the material, so in case the shared defines are updated in
  // MaterialSharedDefines, the material also gets the updated values.
  const sharedDefines = materialSharedDefines.defines;
  for (const key of Object.keys(sharedDefines)) {
    Object.defineProperty(material.defines, key, {
      enumerable: true,
      get() {
        return sharedDefines[key];
      },
    });
  }

  if (materialSharedDefines.materials.indexOf(material) < 0) {
    materialSharedDefines.materials.push(material);
  }
}

export class MaterialSharedDefines {
  readonly defines: SharedDefines;
  readonly materials: THREEMeshMaterial[] = [];

  private toneMappingInternal = TONE_MAPPING_ALGORITHM;

  private wireframeInternal: boolean = false;

  constructor() {
    // These defines are shared between the material instances in this group.
    this.defines = {
      SSFX_MATERIAL_DEV: 0, // Setting this define to 1 enables changing material output type with just a uniform update.
      USE_ADVANCED_PCSS: defaultSoftShadowParams.useAdvancedPCSS ? 1 : 0,
      USE_SMOOTHSTEP_SHADOW_COMPARISON: defaultSoftShadowParams.useSmoothstepShadowComparison ? 1 : 0,
      PCSS_PENUMBRA_AFFECTED_BY_DISTANCE: defaultSoftShadowParams.pcssPenumbraAffectedByDistance ? 1 : 0,
      PCSS_NUM_SAMPLES: defaultSoftShadowParams.pcssNumSamples,
      PCSS_NUM_RINGS: 5,

      ISOLATE_MODE: 1, // Whether the shader supports isolate mode
      PARALLAX_EFFECT_ENABLED: getOS() === OS.IPHONE ? 0 : 1, // Design material parallax effect
      SSAO: 1, // Applying screen space AO
      SSR: 0, // Applying screen space reflections
      SCREEN_SPACE_SHADOWS: 1,
      SMALL_SCALE_AO: 1,
      LIGHT_PROBES_ENABLED: 1,
      SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE: 1,
      HIGHARC_ACES_FILMIC_DIVISOR_A,
      HIGHARC_ACES_FILMIC_DIVISOR_B,
      HIGHARC_ACES_FILMIC_DIVISOR_C,
      HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH,
      DM_LINEAR_TONE_MAPPING: this.toneMappingInternal === THREE.LinearToneMapping ? 1 : 0,
      DM_REINHARD_TONE_MAPPING: this.toneMappingInternal === THREE.ReinhardToneMapping ? 1 : 0,
      DM_CINEON_TONE_MAPPING: this.toneMappingInternal === THREE.CineonToneMapping ? 1 : 0,
      DM_ACES_FILMIC_TONE_MAPPING: this.toneMappingInternal === THREE.ACESFilmicToneMapping ? 1 : 0,
    };
  }

  private recompile() {
    for (const mat of this.materials) {
      mat.wireframe = this.wireframeInternal;
      mat.needsUpdate = true;
    }
  }

  get wireframe(): boolean {
    return this.wireframeInternal;
  }

  set wireframe(value: boolean) {
    if (this.wireframeInternal !== value) {
      this.wireframeInternal = value;
      this.recompile();
    }
  }

  set outputType(outputType: SSFXRendererOutput) {
    if (this.defines.SSFX_MATERIAL_DEV === 0 && outputType !== SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS) {
      this.defines.SSFX_MATERIAL_DEV = 1;
      this.recompile();
    }
  }

  get pcssNumSamples(): number {
    return this.defines.PCSS_NUM_SAMPLES;
  }

  set pcssNumSamples(value: number) {
    if (this.defines.PCSS_NUM_SAMPLES !== value) {
      this.defines.PCSS_NUM_SAMPLES = value;
      // We automatically pick a PCSS_NUM_RINGS value that gives an even sample distribution with the
      // specified sample count (up to 23 samples).
      let pcssNumRings = 5;
      switch (value) {
        case 10:
        case 5:
          pcssNumRings = 3;
          break;
        case 20:
        case 16:
        case 14:
          pcssNumRings = 6;
          break;
        case 21:
        case 19:
        case 11:
          pcssNumRings = 8;
          break;
        case 9:
          pcssNumRings = 7;
          break;
        case 15:
          pcssNumRings = 28;
          break;
      }
      this.defines.PCSS_NUM_RINGS = pcssNumRings;
      this.recompile();
    }
  }

  get toneMapping(): THREE.ToneMapping {
    return this.toneMappingInternal;
  }

  set toneMapping(value: THREE.ToneMapping) {
    if (this.toneMappingInternal !== value) {
      setToneMappingDefine(this.defines, value);
      this.recompile();
      this.toneMappingInternal = value;
    }
  }

  private getSharedBooleanDefine(defineName: BooleanSharedDefineKey): boolean {
    return this.defines[defineName] === 1;
  }

  private setSharedBooleanDefine(defineName: BooleanSharedDefineKey, value: boolean) {
    const intValue: BooleanDefineValue = value ? 1 : 0;
    if (this.defines[defineName] !== intValue) {
      this.defines[defineName] = intValue;
      this.recompile();
    }
  }

  get ssaoEnabled(): boolean {
    return this.getSharedBooleanDefine("SSAO");
  }
  set ssaoEnabled(value: boolean) {
    this.setSharedBooleanDefine("SSAO", value);
  }

  get ssrEnabled(): boolean {
    return this.getSharedBooleanDefine("SSR");
  }
  set ssrEnabled(value: boolean) {
    this.setSharedBooleanDefine("SSR", value);
  }

  get screenSpaceShadowsEnabled(): boolean {
    return this.getSharedBooleanDefine("SCREEN_SPACE_SHADOWS");
  }
  set screenSpaceShadowsEnabled(value: boolean) {
    this.setSharedBooleanDefine("SCREEN_SPACE_SHADOWS", value);
  }

  get smallScaleAOEnabled(): boolean {
    return this.getSharedBooleanDefine("SMALL_SCALE_AO");
  }
  set smallScaleAOEnabled(value: boolean) {
    this.setSharedBooleanDefine("SMALL_SCALE_AO", value);
  }

  get subMissingReflectionWithIrradiance(): boolean {
    return this.getSharedBooleanDefine("SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE");
  }
  set subMissingReflectionWithIrradiance(value: boolean) {
    this.setSharedBooleanDefine("SUBSTITUTE_MISSING_REFLECTION_WITH_IRRADIANCE", value);
  }

  get lightProbesEnabled(): boolean {
    return this.getSharedBooleanDefine("LIGHT_PROBES_ENABLED");
  }
  set lightProbesEnabled(value: boolean) {
    this.setSharedBooleanDefine("LIGHT_PROBES_ENABLED", value);
  }

  get useAdvancedPCSS(): boolean {
    return this.getSharedBooleanDefine("USE_ADVANCED_PCSS");
  }
  set useAdvancedPCSS(value: boolean) {
    this.setSharedBooleanDefine("USE_ADVANCED_PCSS", value);
  }

  get useSmoothstepShadowComparison(): boolean {
    return this.getSharedBooleanDefine("USE_SMOOTHSTEP_SHADOW_COMPARISON");
  }
  set useSmoothstepShadowComparison(value: boolean) {
    this.setSharedBooleanDefine("USE_SMOOTHSTEP_SHADOW_COMPARISON", value);
  }

  get pcssPenumbraAffectedByDistance(): boolean {
    return this.getSharedBooleanDefine("PCSS_PENUMBRA_AFFECTED_BY_DISTANCE");
  }
  set pcssPenumbraAffectedByDistance(value: boolean) {
    this.setSharedBooleanDefine("PCSS_PENUMBRA_AFFECTED_BY_DISTANCE", value);
  }

  get isolateModeSupported(): boolean {
    return this.getSharedBooleanDefine("ISOLATE_MODE");
  }
  set isolateModeSupported(value: boolean) {
    this.setSharedBooleanDefine("ISOLATE_MODE", value);
  }

  get parallaxEffectEnabled(): boolean {
    return this.getSharedBooleanDefine("PARALLAX_EFFECT_ENABLED");
  }
  set parallaxEffectEnabled(value: boolean) {
    this.setSharedBooleanDefine("PARALLAX_EFFECT_ENABLED", value);
  }
}
